var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
const debug = false;
const allowedMuteTypes = ['source', 'output', 'crossPoint', 'group', 'zone'];
const allowedVolumeTypes = ['source', 'output', 'crossPoint', 'group'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: ['preset'],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
function isEclerAudioChannelConfig(config) {
    return typeof config === 'string';
}
function isEclerContactClosureConfig(config) {
    return 'pin' in config && typeof config.pin === 'number';
}
function isEclerPresetMuteConfig(config) {
    return (typeof config === 'object' &&
        'mutePreset' in config &&
        typeof config.mutePreset === 'string' &&
        'muteStatus' in config &&
        typeof config.muteStatus === 'string' &&
        'unmutePreset' in config &&
        typeof config.unmutePreset === 'string');
}
function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
class Channel {
    constructor(type, key, log) {
        this.type = type;
        this.key = key;
        this.log = log;
        this.chanNumber = 0;
        this.isMuteConfigured = false;
        this.isVolumeConfigured = false;
        this.name = '';
        this.inputNum = 0; // used with crosspoints
        this.outputNum = 0; // used with crosspoints
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    loadPreset(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`preset not supported for ${this.key}`);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`fader not supported for ${this.key}`);
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`mute not supported for ${this.key}`);
        });
    }
    isAllowed(filter) {
        return allowedTypes[filter].includes(this.type);
    }
    matches(key) {
        return this.key === key;
    }
}
class Channels {
    constructor(log) {
        this.log = log;
        this.channels = [];
    }
    addChannel(chan) {
        this.channels.push(chan);
    }
    get areAnyConfigured() {
        for (const channel of this.channels) {
            if (channel.isMuteConfigured || channel.isVolumeConfigured)
                return true;
        }
        return false;
    }
    clearChannels() {
        this.channels.length = 0;
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: type === 'preset',
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    getAllConfiguredChannels() {
        return this.channels.filter(channel => channel.isMuteConfigured || channel.isVolumeConfigured);
    }
    getByKey(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getChannel(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel;
        }
        return undefined;
    }
    getChannelType(id) {
        for (const channel of this.channels) {
            if (channel.matches(id))
                return channel === null || channel === void 0 ? void 0 : channel.type;
        }
        return undefined;
    }
    getConfiguredChannels(control) {
        return this.channels.filter(channel => control === 'mute' ? channel.isMuteConfigured : channel.isVolumeConfigured);
    }
    getDetected() {
        const detectedChannels = [];
        for (const channel of this.channels) {
            const detectedChannel = {
                channel: channel.key,
                type: channel.type,
                name: channel.name,
                filters: this.computeFilters(channel.type),
            };
            if (channel.name)
                detectedChannel.name = channel.name;
            detectedChannels.push(detectedChannel);
        }
        return detectedChannels;
    }
}
// Crosspoints are the control points between an input and a mixer output.
class CrossPointChannel extends Channel {
    constructor(inputNum, outputNum, log) {
        super('crossPoint', `input.${inputNum}.output.${outputNum}`, log);
        this.inputNum = inputNum;
        this.outputNum = outputNum;
        this.name = `Source ${inputNum} to `;
        if (outputNum === 5 || outputNum === 6) {
            this.name += `Aux Out ${outputNum - 4}`;
        }
        else if (outputNum > 6) {
            this.name += `Digital Out ${outputNum - 6}`;
        }
        else {
            this.name += `Amp Out ${outputNum}`;
        }
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET XMUTE ${this.inputNum} ${this.outputNum}\n`, /DATA XMUTE\s+(\d+)\s+(\d+)\s+(YES|NO)/i, tags);
            return resp.match[3].toLowerCase() === 'yes';
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET XMUTE ${this.inputNum} ${this.outputNum} ${value ? 'YES' : 'NO'}\n`, tags);
            return value;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET XLEVEL ${this.inputNum} ${this.outputNum}\n`, /DATA XLEVEL\s+(\d+)\s+(\d+)\s+(\d+)/i, tags);
            return parseInt(resp.match[3], 10);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET XLEVEL ${this.inputNum} ${this.outputNum} ${value}\n`, tags);
            return value;
        });
    }
}
class GroupChannel extends Channel {
    constructor(id, groupType, log) {
        super('group', `group.${groupType.toLowerCase()}.${id}`, log);
        if (id === 1 && groupType.toLowerCase() === 'gen') {
            this.name = 'General Volume';
        }
        else {
            this.name = `Group ${capitalizeFirstLetter(groupType)} ${id}`;
        }
        this.chanNumber = id;
        this.groupType = groupType;
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET GMUTE ${this.groupType}${this.groupType === 'Gen' ? '' : ' ' + this.chanNumber}\n`, new RegExp(`DATA GMUTE\\s+${this.groupType}\\s*(\\d+)?\\s+(YES|NO)`, 'i'), tags);
            return resp.match[2].toLowerCase() === 'yes';
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET GMUTE ${this.groupType} ${this.chanNumber} ${value ? 'YES' : 'NO'}\n`);
            return value;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // GLEVEL Gen doesn't use an index parameter
            const resp = yield socket.send(`GET GLEVEL ${this.groupType} ${this.chanNumber}\n`, new RegExp(`DATA\\s+GLEVEL\\s+${this.groupType}\\s*(\\d+)?\\s+(\\d+)`, 'i'), tags);
            return parseInt(resp.match[2], 10);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET GLEVEL ${this.groupType} ${this.chanNumber} ${value}\n`, tags);
            return value;
        });
    }
}
class OutputChannel extends Channel {
    constructor(id, log) {
        super('output', `output.${id}`, log);
        if (id === 5 || id === 6) {
            this.name = `Aux Out ${id - 4}`;
        }
        else if (id > 6) {
            this.name = `Digital Out ${id - 6}`;
        }
        else {
            this.name = `Amp Out ${id}`;
        }
        this.chanNumber = id;
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET OMUTE ${this.chanNumber}\n`, /DATA OMUTE (\d+)\s+(YES|NO)/, tags);
            return resp.match[2].toLowerCase() === 'yes';
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET OMUTE ${this.chanNumber} ${value ? 'YES' : 'NO'}\n`);
            return value;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET OLEVEL ${this.chanNumber}\n`, /DATA OLEVEL (\d+)\s+(\d+)/i, tags);
            return parseInt(resp.match[2]);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET OLEVEL ${this.chanNumber} ${value}\n`, tags);
            return value;
        });
    }
}
class PresetChannel extends Channel {
    constructor(id, log) {
        super('preset', `preset.${id}`, log);
        this.name = `Preset ${id}`;
        this.chanNumber = id;
    }
    loadPreset(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET PRESET ${this.chanNumber}\n`, tags); // no response
        });
    }
}
class SourceChannel extends Channel {
    constructor(id, log) {
        super('source', `source.${id}`, log);
        this.name = `Source ${id}`;
        this.chanNumber = id; // one-based indexing
    }
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET SMUTE ${this.chanNumber}\n`, /DATA SMUTE (\d+)\s+(YES|NO)/i, tags);
            return resp.match[2].toLowerCase() === 'yes';
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET SMUTE ${this.chanNumber} ${value ? 'YES' : 'NO'}\n`, tags);
            return value;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET SLEVEL ${this.chanNumber}\n`, /DATA SLEVEL (\d+)\s+(\d+)/i, tags);
            return parseInt(resp.match[2]);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET SLEVEL ${this.chanNumber} ${value}\n`, tags);
            return value;
        });
    }
}
// Used for source selection of a particular source to a particular output.
// Each zone selection is managed as a mute of a zone output / source input crosspoint.
class ZoneOutputChannel extends Channel {
    constructor(id, src, srcType, log) {
        super('zone', `zone.${id}.${srcType}${src}`, log);
        this.chanNumber = id; // the zone number
        this.src = `${src}`; // the specific source
        this.srcType = `${srcType}`; // input type
        this.name = `Zone ${id}: Select ${srcType}${src}`;
    }
    // See if this source is selected: returns false (i.e., unmuted) if this particular source is
    // selected.
    getMute(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield socket.send(`GET ZONERIN ${this.chanNumber}\n`, /DATA\s+ZONERIN\s+(\d+)\s+([SOA]\d+)/i, tags);
            return !(resp.match[2] === `${this.srcType}${this.src}`);
        });
    }
    setMute(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // Select the source when unmuting.
            if (value === false) {
                yield socket.sendAsync(`SET ZONERIN ${this.chanNumber} ${this.srcType}${this.src}\n`);
            }
            return value;
        });
    }
    getFader(socket, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use output level for the zone, ignore source selection.
            const resp = yield socket.send(`GET OLEVEL ${this.chanNumber}\n`, /DATA OLEVEL (\d+)\s+(\d+)/i, tags);
            return parseInt(resp.match[2]);
        });
    }
    setFader(socket, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield socket.sendAsync(`SET OLEVEL ${this.chanNumber} ${value}\n`, tags);
            return value;
        });
    }
}
class EclerChannels extends Channels {
    constructor(numInputs, numOutputs, xpointIns, xpointOuts, groups, numPresets, numZones, log) {
        super(log);
        for (let inp = 1; inp <= numInputs; inp++) {
            this.channels.push(new SourceChannel(inp, log));
        }
        for (let outp = 1; outp <= numOutputs; outp++) {
            this.channels.push(new OutputChannel(outp, log));
        }
        for (let inp = 1; inp <= xpointIns; inp++) {
            for (let outp = 1; outp <= xpointOuts; outp++) {
                this.channels.push(new CrossPointChannel(inp, outp, log));
            }
        }
        // 0 is factory default preset, 1 - 10 are user presets
        for (let i = 0; i <= numPresets; i++) {
            this.channels.push(new PresetChannel(i, log));
        }
        for (const [key, value] of Object.entries(groups !== null && groups !== void 0 ? groups : {})) {
            for (let i = 1; i <= value; i++) {
                this.channels.push(new GroupChannel(i, key, log));
            }
        }
        // Create zone source selections channels that can be unmuted to select that source to the
        // particular zone output.
        //
        // TODO: once we support audio switchers, change implementation of zones.
        for (let zone = 1; zone <= numZones; zone++) {
            // Zones 1 - 4 only support input sources.
            for (let i = 1; i <= numInputs; i++) {
                this.channels.push(new ZoneOutputChannel(zone, i, 'S', log)); // Source inputs
            }
            if (zone >= 5) {
                // Add outputs as possible source selections to zones 5 and 6 (Aux outputs).
                for (let o = 1; o <= 4; o++) {
                    this.channels.push(new ZoneOutputChannel(zone, o, 'O', log)); // Outputs
                }
            }
            if (zone >= 7) {
                // Zones 7 - 10 also support the Aux signals as source selections.
                for (let aux = 1; aux <= 2; aux++) {
                    this.channels.push(new ZoneOutputChannel(zone, aux, 'A', log)); // Aux inputs
                }
            }
        }
    }
}
export class EclerVida {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        this.channelPollIndex = 0;
        this.configuredChannels = [];
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.contactClosure = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.socket.send(`GET GPO ${key}\n`, /DATA GPO\s+(\d)\s+(YES|NO)/i, tags);
                return resp.match[2] === 'YES';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                // Sets don't return a value
                yield this.socket.sendAsync(`SET GPO ${key} ${value ? 'YES' : 'NO'}\n`, tags);
                return value;
            }),
            getKey: (config) => {
                if (!isEclerContactClosureConfig(config)) {
                    throw new Error(`invalid contact closure config: ${JSON.stringify(config)}`);
                }
                return config.pin.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isEclerContactClosureConfig(config)) {
                    this.log.error(`invalid contact closure config: ${JSON.stringify(config)}`);
                    return { pin: 'missing' };
                }
                if (config.pin < 1 || config.pin > 4)
                    return { pin: 'range' };
                return { pin: 'ok' };
            }),
            asyncHandlers: {
                regExp: /DATA\s+GPO\s+(\d)\s+(YES|NO)/i,
                callback: (match) => {
                    const key = match[1];
                    const value = match[2].toLowerCase() === 'yes';
                    const configuredKeys = Object.keys(this.state.contactClosures.values);
                    if (key in configuredKeys) {
                        this.state.contactClosures.update(key, value);
                    }
                },
            },
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.getDetected();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getMute(this.socket, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                if (channel.presetMute === undefined) {
                    const retValue = yield channel.setMute(this.socket, value, tags);
                    return retValue !== null && retValue !== void 0 ? retValue : this.state.mutes.values[key];
                }
                else {
                    if (value) {
                        yield channel.presetMute.mute.loadPreset(this.socket, tags);
                    }
                    else {
                        yield channel.presetMute.unmute.loadPreset(this.socket, tags);
                    }
                    return value;
                }
            }),
            asyncHandlers: [
                {
                    regExp: /DATA\s+(OMUTE|SMUTE|GMUTE)\s+(Gen|Loc|Net)?\s*(\d+)?\s+(YES|NO)/i,
                    callback: (match) => {
                        var _a;
                        const chan = (_a = match[3]) !== null && _a !== void 0 ? _a : '1';
                        const value = match[4].toLowerCase() === 'yes';
                        const label = match[1] === 'OMUTE' ? 'output' : match[1] === 'SMUTE' ? 'source' : 'group';
                        const key = `${label}.${match[2] ? match[2].toLowerCase() + '.' : ''}${chan}`;
                        if (key in this.state.mutes.values) {
                            this.state.mutes.update(key, value);
                        }
                    },
                },
                {
                    // crosspoint mutes
                    regExp: /DATA\s+XMUTE\s+(\d+)\s+(\d+)\s+(YES|NO)/i,
                    callback: (match) => {
                        const inchan = match[1];
                        const outchan = match[2];
                        const value = match[3].toLowerCase() === 'yes';
                        const key = `input.${inchan}.output.${outchan}`;
                        if (key in this.state.mutes.values) {
                            this.state.mutes.update(key, value);
                        }
                    },
                },
            ],
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.socket.send(`GET POWER\n`, /DATA POWER (SLEEPING|RUNNING)/i, tags);
                return resp.match[1]
                    ? resp.match[1].toLowerCase() === 'running'
                    : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                this.framework.suspendPolling(5000);
                yield this.socket.sendAsync(`SET POWER ${power ? 'ON' : 'OFF'}\n`, tags);
                this.busyTimeout.set(() => this.state.busy.update(false), 5000);
                return power;
            }),
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                return yield channel.getFader(this.socket, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    throw new Error(`no channel for key = ${key}`);
                yield this.syncVolumeMute(channel, value);
                if (debug)
                    this.log.debug(`driver: set volume: key: ${key}, volume: ${value}`);
                return yield channel.setFader(this.socket, value, tags);
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (debug) {
                    this.log.debug(`range: config = ${JSON.stringify(config)}, info = ${JSON.stringify(info)}  `);
                }
                const isXp = this.channels.getChannelType(config) === 'crossPoint';
                if (isXp) {
                    if (debug)
                        this.log.debug(`isXp in volume`);
                    if (info.volumeType === 'trim')
                        return { max: 100, min: 40, round: 1 };
                    return { max: 100, min: 14, round: 1 }; // volume 0 to -50 dB
                }
                else {
                    if (info.volumeType === 'trim')
                        return { max: 100, min: 40, round: 1 };
                    return { max: 100, min: 14, round: 1 }; // volume 0 to -50 dB
                }
            }),
            asyncHandlers: [
                {
                    regExp: /DATA\s+(GLEVEL|OLEVEL|SLEVEL)\s+(Gen|Loc|Net)?\s*(\d+)?\s+(\d+)/i,
                    callback: (match) => {
                        var _a;
                        const chan = (_a = match[3]) !== null && _a !== void 0 ? _a : '1';
                        const value = parseInt(match[4], 10);
                        const label = match[1] === 'OLEVEL'
                            ? 'output'
                            : match[1] === 'SLEVEL'
                                ? 'source'
                                : 'group';
                        const key = `${label}.${match[2] ? match[2].toLowerCase() + '.' : ''}${chan}`;
                        if (key in this.state.volumes.values) {
                            this.state.volumes.update(key, value);
                        }
                    },
                },
                {
                    regExp: /DATA\s+XLEVEL\s+(\d+)\s+(\d+)\s+(\d+)/i,
                    callback: (match) => {
                        const inchan = match[1];
                        const outchan = match[2];
                        const value = parseInt(match[3]);
                        const key = `input.${inchan}.output.${outchan}`;
                        if (key in this.state.volumes.values) {
                            this.state.volumes.update(key, value);
                        }
                    },
                },
            ],
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                // Levels are updated with async handlers, mutes don't always generate a response from
                // the device.
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.configuredChannels.length > 0) {
                        const channel = this.configuredChannels[this.channelPollIndex];
                        if (channel !== undefined) {
                            const mute = yield channel.getMute(this.socket, ['poll']);
                            if (channel.isMuteConfigured) {
                                this.state.mutes.update(channel.key, mute);
                            }
                        }
                        this.channelPollIndex++;
                        if (this.channelPollIndex >= this.configuredChannels.length) {
                            this.channelPollIndex = 0;
                        }
                    }
                    else {
                        // If no channels are configured, query the power as a dummy poll to prevent an
                        // idle timeout.
                        yield this.socket.send(`GET POWER\n`, /POWER|connected/i, ['poll']);
                    }
                }),
            },
        ];
        this.channels = new EclerChannels(0, 0, 0, 0, {}, 0, 0, log); // placeholder
        this.busyTimeout = this.timeouts.create();
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.channels.clearChannels();
            yield this.getDetectedChannels(['configure']);
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            this.configuredChannels.length = 0;
            this.channelPollIndex = 0;
            this.configuredChannels.push(...this.channels.getAllConfiguredChannels());
            if (debug) {
                this.log.debug(`configured Channels = ${JSON.stringify(this.configuredChannels, null, 4)}`);
            }
        });
    }
    onConnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            // release the socket connection
            yield this.socket.send(`SYSTEM DISCONNECT\n`, /DISCONNECT|not connected|unconnected/i, [
                'configure',
            ]);
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            let result = 'verifyFailed';
            // Try to connect with a standard connection request without closing other connections.
            const startCommunication = yield this.socket.send('SYSTEM CONNECT\n', /(DATA INFO_NAME)|(maximum)/i, ['initialize']);
            if (startCommunication.match[1]) {
                result = 'connected';
            }
            else if (startCommunication.match[2]) {
                // If we reached the maximum number of connections, use the master option to force all
                // other socket connections to close.
                const retryCommunication = yield this.socket.send('SYSTEM CONNECT MASTER ONCE\n', /(DATA INFO_NAME)|(maximum)/i, ['initialize']);
                if (retryCommunication.match[1]) {
                    result = 'connected';
                }
                else {
                    result = 'authFailed';
                }
            }
            return result;
        });
    }
    getDetectedChannels(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                let numInputChannels = 0;
                let numOutputChannels = 0;
                let numZoneChannels = 0;
                let xpointIns = 0;
                let xpointOuts = 0;
                const groups = { Loc: 0, Gen: 0, Net: 0 };
                const detectTimeout = this.timeouts.create();
                const fwVersion = this.socket.registerAsync(/DATA\s+INFO_VERSION\s+(.+)\n/i, (match) => {
                    this.state.firmwareVersion.update(match[1]);
                    detectTimeout.restart(1000);
                });
                // Find the maximum number of inputs and outputs.
                const xpoints = this.socket.registerAsync(/DATA\s+XMUTE\s+(\d+)\s+(\d+)\s+(YES|NO)/i, (match) => {
                    const xin = parseInt(match[1], 10);
                    const xout = parseInt(match[2], 10);
                    if (xin > xpointIns)
                        xpointIns = xin;
                    if (xout > xpointOuts)
                        xpointOuts = xout;
                    detectTimeout.restart(1000);
                });
                // Find the number of sources and outputs.
                const channel = this.socket.registerAsync(/DATA\s+(OLEVEL|SLEVEL)\s+(.+)/i, (match) => {
                    const chanNum = parseInt(match[2], 10);
                    if (match[1] === 'SLEVEL' && chanNum > numInputChannels) {
                        numInputChannels = chanNum;
                    }
                    else if (match[1] === 'OLEVEL' && chanNum > numOutputChannels) {
                        numOutputChannels = chanNum;
                    }
                    detectTimeout.restart(1000);
                });
                // Find the max numbers of the different groups: Loc, Gen, Net.
                const group = this.socket.registerAsync(/DATA\s+GLEVEL\s+(Loc|Gen|Net)\s+(\d+)?\s*(\d+)/i, (match) => {
                    const gtype = match[1];
                    const chanNum = match[2] ? parseInt(match[2], 10) : 0;
                    if (gtype === 'Gen') {
                        groups[gtype] = 1;
                    }
                    else if (chanNum > groups[gtype]) {
                        groups[gtype] = chanNum;
                    }
                    detectTimeout.restart(1000);
                });
                // Find zone output definitions
                const zone = this.socket.registerAsync(/DATA\s+ZONERIN\s+(.+)/i, (match) => {
                    const chanNum = parseInt(match[2], 10);
                    if (chanNum > numZoneChannels) {
                        numZoneChannels = chanNum;
                    }
                    detectTimeout.restart(1000);
                });
                // GET ALL returns with a full status update that we can use to determine the input,
                // output, and crosspoint channels.
                this.socket.sendAsync('GET ALL\n', ['configure']);
                detectTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                    if (debug) {
                        this.log.debug(`ins and outs = ${[numInputChannels, numOutputChannels]}, matrix: ${[
                            xpointIns,
                            xpointOuts,
                        ]}, groups: ${JSON.stringify(groups)}, zone outputs: ${numZoneChannels}`);
                    }
                    this.socket.unregisterAsync(fwVersion);
                    this.socket.unregisterAsync(xpoints);
                    this.socket.unregisterAsync(channel);
                    this.socket.unregisterAsync(group);
                    this.socket.unregisterAsync(zone);
                    this.channels = new EclerChannels(numInputChannels, numOutputChannels, xpointIns, xpointOuts, groups, numZoneChannels, 10, // number of presets
                    this.log);
                    if (debug)
                        this.log.debug(`channels = ${JSON.stringify(this.channels, null, 4)}`);
                    resolve(this.channels);
                }), 2000);
            });
        });
    }
    getKey(config) {
        if (isEclerPresetMuteConfig(config))
            return config.muteStatus;
        if (isEclerAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    hasRoomVolumeMute() {
        if (this.volumeConfig) {
            const chan = this.channels.getByKey(this.volumeConfig);
            return (chan === null || chan === void 0 ? void 0 : chan.type) ? allowedMuteTypes.includes(chan.type) : false;
        }
        else {
            return false;
        }
    }
    syncVolumeMute(channel, volume) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.volumeConfig === undefined || !this.hasRoomVolumeMute())
                return;
            const key = this.getKey(this.volumeConfig);
            const newVolumeMute = volume <= 14;
            if (channel.key !== key)
                return;
            if (this.state.mutes.values[key] !== newVolumeMute) {
                const value = yield this.mute.set(key, newVolumeMute, ['set.volumeMute']);
                this.state.mutes.update(key, value);
            }
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            if (debug) {
                this.log.debug(`inside validate for key: ${key}, config: ${JSON.stringify(config)}, info: ${JSON.stringify(info)}`);
            }
            if (isEclerPresetMuteConfig(config)) {
                const muteStatusChannel = this.channels.getByKey(config.muteStatus);
                const mutePresetChannel = this.channels.getByKey(config.mutePreset);
                const unmutePresetChannel = this.channels.getByKey(config.unmutePreset);
                if (muteStatusChannel === undefined ||
                    mutePresetChannel === undefined ||
                    unmutePresetChannel === undefined) {
                    return 'missing';
                }
                if (muteStatusChannel.type === 'preset' ||
                    mutePresetChannel.type !== 'preset' ||
                    unmutePresetChannel.type !== 'preset') {
                    return 'incompatible';
                }
                // Configure the mute and unmute presets.
                if (muteStatusChannel) {
                    muteStatusChannel.isMuteConfigured = true;
                    muteStatusChannel.presetMute = {
                        mute: mutePresetChannel,
                        unmute: unmutePresetChannel,
                    };
                }
                return 'ok';
            }
            else if (isEclerAudioChannelConfig(config)) {
                if (info.type === 'volume' && info.filter === 'amplifier')
                    this.volumeConfig = config;
                const channel = this.channels.getByKey(key);
                if (channel === undefined)
                    return 'missing';
                if (channel.type === 'preset')
                    return 'incompatible';
                if (info.type === 'volume') {
                    channel.isVolumeConfigured = true;
                    // Check if volume mute can be supported by the channel.
                    if (config === this.volumeConfig && allowedMuteTypes.includes(channel.type)) {
                        channel.isMuteConfigured = true;
                    }
                }
                else if (info.type === 'mute') {
                    if (allowedMuteTypes.includes(channel.type))
                        channel.isMuteConfigured = true;
                }
                if (debug)
                    this.log.debug(`validChannel: ${JSON.stringify(config, null, 4)}`);
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
EclerVida.channelsConfigName = 'eclerVidaControlChannels';
EclerVida.driverType = 'udpDriver';
EclerVida.key = '-O339wPeb6O6jf1ZIRlq';
EclerVida.socketOptions = [{ port: 5800 }];
